/**
 * A person's name — the SPA's mirror of the server's one rule
 * (backend/shared/services/person_name.py), so a form can answer as the person
 * types. The server is the authority: it re-checks every rename and its error
 * code wins.
 *
 * Trimmed, inner whitespace collapsed; 2–100 characters with at least 2 letters
 * in any script; besides letters only combining marks, spaces, hyphens
 * (incl. the Hebrew maqaf), apostrophes (' ’ ׳) and periods; no digits,
 * emails, web addresses, control or bidi/format characters.
 */

export type PersonNameError =
  | 'NAME_REQUIRED'
  | 'NAME_TOO_SHORT'
  | 'NAME_TOO_LONG'
  | 'NAME_NEEDS_LETTERS'
  | 'NAME_LOOKS_LIKE_EMAIL'
  | 'NAME_LOOKS_LIKE_URL'
  | 'NAME_INVALID_CHARACTERS';

export const PERSON_NAME_ERRORS: ReadonlySet<string> = new Set<PersonNameError>([
  'NAME_REQUIRED', 'NAME_TOO_SHORT', 'NAME_TOO_LONG', 'NAME_NEEDS_LETTERS',
  'NAME_LOOKS_LIKE_EMAIL', 'NAME_LOOKS_LIKE_URL', 'NAME_INVALID_CHARACTERS',
]);

export const PERSON_NAME_MAX_LENGTH = 100;
const MIN_LENGTH = 2;
const MIN_LETTERS = 2;

// Hyphen-minus, hyphen, non-breaking hyphen, Hebrew maqaf; apostrophe, right
// single quotation mark, Hebrew geresh; period.
const ALLOWED_PUNCTUATION = new Set(['-', '‐', '‑', '־', "'", '’', '׳', '.']);
const URL = /[a-z][a-z0-9+.-]*:\/\/|\bwww\./i;
const DOMAIN = /(?<![\p{L}\p{N}_.])[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,24}(?![\p{L}\p{N}_.])/u;
const CONTROL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u;
const LETTER = /\p{L}/u;
const MARK = /\p{M}/u;

/** Trimmed, every run of whitespace as one space — as the server stores it. */
export function tidyPersonName(raw: string): string {
  return raw.trim().split(/\s+/u).filter(Boolean).join(' ');
}

/** The name as it would be stored, or why it cannot be used. */
export function checkPersonName(raw: string): { name: string; error: PersonNameError | null } {
  const withoutSpaces = raw.replace(/\s/gu, '');
  if (CONTROL_OR_FORMAT.test(withoutSpaces)) return { name: '', error: 'NAME_INVALID_CHARACTERS' };
  const name = tidyPersonName(raw);
  if (!name) return { name, error: 'NAME_REQUIRED' };
  const length = [...name].length;
  if (length > PERSON_NAME_MAX_LENGTH) return { name, error: 'NAME_TOO_LONG' };
  if (length < MIN_LENGTH) return { name, error: 'NAME_TOO_SHORT' };
  if (name.includes('@')) return { name, error: 'NAME_LOOKS_LIKE_EMAIL' };
  if (URL.test(name) || DOMAIN.test(name)) return { name, error: 'NAME_LOOKS_LIKE_URL' };
  for (const ch of name) {
    if (LETTER.test(ch) || MARK.test(ch) || ch === ' ' || ALLOWED_PUNCTUATION.has(ch)) continue;
    return { name, error: 'NAME_INVALID_CHARACTERS' };
  }
  if ([...name].filter(ch => LETTER.test(ch)).length < MIN_LETTERS) return { name, error: 'NAME_NEEDS_LETTERS' };
  return { name, error: null };
}

/** The error code a failed rename answered with (the server's word wins). */
export function personNameErrorCode(err: unknown): string {
  const data = (err as { response?: { data?: { error_code?: string } } })?.response?.data;
  return data?.error_code || 'RENAME_FAILED';
}
